import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconUsers, 
  IconRoute2, 
  IconTractor, 
  IconHistory, 
  IconCloudSnow, 
  IconMessageCircle, 
  IconFileText, 
  IconCalendarStats, 
  IconReportAnalytics, 
  IconMail,
  IconCheck
} from '@tabler/icons-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area } from 'recharts';
import styles from './Operations.module.css';

const Operations = () => {
  const navigate = useNavigate();

  // Operation cards data
  const operationsCards = [
    { icon: <IconUsers size={24} />, label: 'Walkway Crews', route: '/walkwayCrewDispatch' },
    { icon: <IconRoute2 size={24} />, label: 'Salting Routes', route: '/SaltingRouteDispatch' },
    { icon: <IconTractor size={24} />, label: 'Loaders', route: '/loaderDispatch' },
    { icon: <IconHistory size={24} />, label: 'History', route: '/dispatchHistory' },
    { icon: <IconCloudSnow size={24} />, label: 'Storms', route: '/storms' },
    { icon: <IconMessageCircle size={24} />, label: 'Messages', route: '/messages' }
  ];

  // Quick actions data
  const quickActions = [
    { icon: <IconFileText size={18} />, label: 'View Saved Dispatches', action: () => navigate('/saved-dispatches') },
    { icon: <IconCalendarStats size={18} />, label: 'Download Dispatch History Reports', action: () => navigate('/dispatch-reports') },
    { icon: <IconReportAnalytics size={18} />, label: 'Generate Regional Warning Report', action: () => navigate('/regional-warnings') },
    { icon: <IconMail size={18} />, label: 'Email Territory Managers', action: () => navigate('/email-managers') }
  ];

  // Mock data for messages analytics
  const analyticsData = {
    thisWeek: { count: 12, change: -6 },
    thisYear: { count: 762, change: 15 },
    unread: { count: 62, change: 7 }
  };

  // Mock data for the chart
  const chartData = [
    { name: '07 Am', value: 30 },
    { name: '08 Am', value: 40 },
    { name: '09 Am', value: 75 },
    { name: '10 Am', value: 100 },
    { name: '11 Am', value: 65 },
    { name: '12 Pm', value: 35 },
    { name: '01 Pm', value: 45 },
    { name: '02 Pm', value: 85 },
    { name: '03 Pm', value: 110 },
  ];

  // Mock data for dispatch history
  const dispatchHistory = [
    {
      id: 1,
      type: 'walkway',
      title: 'Walkway Crew Dispatched to Bolton',
      time: '12h Ago - Feb 12, 2025 12:09 PM EST',
      status: 'unread'
    },
    {
      id: 2,
      type: 'loader',
      title: 'Loader Dispatched to Mississauga',
      time: '7h Ago - Feb 12, 2025 12:09 PM EST',
      status: 'confirmed'
    },
    {
      id: 3,
      type: 'salting',
      title: 'Salting Route Dispatched',
      time: '7h Ago - Feb 12, 2025 12:09 PM EST',
      status: 'unread'
    },
    {
      id: 4,
      type: 'walkway',
      title: 'Walkway Crew Dispatched to Bolton',
      time: '7h Ago - Feb 12, 2025 12:09 PM EST',
      status: 'denied'
    }
  ];

  // Mock data for staff status notifications
  const staffStatus = [
    {
      id: 1,
      name: 'Sami Hormiz',
      details: 'Storm ID: 57 Type: snow - Date: March 6th 2025',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'confirmed'
    },
    {
      id: 2,
      name: 'Ramon Arkan (Sargon)',
      details: 'Dispatch id: 1109 - Type: Walkway - for March 6th 2025 2:00am',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'confirmed'
    },
    {
      id: 3,
      name: 'Hassan Aziz',
      details: 'Dispatch id: 1109 - Type: Walkway - for March 6th 2025 2:00am',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'denied'
    },
    {
      id: 4,
      name: 'George Codorniz',
      details: 'Storm ID: 57 Type: snow - Date: March 6th 2025',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'confirmed'
    }
  ];

  // Render status badge based on status
  const renderStatusBadge = (status) => {
    if (status === 'unread') return <span className={styles.statusUnread}>Unread</span>;
    if (status === 'confirmed') return <span className={styles.statusConfirmed}>Confirmed</span>;
    if (status === 'denied') return <span className={styles.statusDenied}>Denied</span>;
    return null;
  };

  // Render icon based on dispatch type
  const renderTypeIcon = (type) => {
    if (type === 'walkway') return (
      <div className={`${styles.historyIcon} ${styles.walkwayIcon}`}>
        <IconUsers size={18} stroke={1.5} />
      </div>
    );
    if (type === 'loader') return (
      <div className={`${styles.historyIcon} ${styles.loaderIcon}`}>
        <IconTractor size={18} stroke={1.5} />
      </div>
    );
    if (type === 'salting') return (
      <div className={`${styles.historyIcon} ${styles.saltingIcon}`}>
        <IconRoute2 size={18} stroke={1.5} />
      </div>
    );
    return null;
  };

  return (
    <div className={styles.operations}>
      <div className={styles.header}>
        <h2><IconFileText size={24} style={{ marginRight: '8px' }} /> Operations</h2>
        <p>Dispatch your crews and proactively communicate with your staff. Utilize reporting features to summarize Dispatch.</p>
      </div>

      {/* Operation cards */}
      <div className={styles.cardsContainer}>
        {operationsCards.map((card, index) => (
          <button 
            key={index} 
            className={styles.operationCard}
            onClick={() => navigate(card.route)}
          >
            <span className={styles.cardLabel}>{card.label}</span>
            <div className={styles.cardIcon}>{card.icon}</div>
          </button>
        ))}
      </div>

      <div className={styles.lowerSection}>
        <div className={styles.leftColumn}>
          <div className={styles.messagesAnalytics}>
            <h3>Messages Analytics</h3>
            <div className={styles.metrics}>
              <div className={styles.metric}>
                <div className={styles.metricChange} data-trend="down">
                  -{Math.abs(analyticsData.thisWeek.change)}%
                </div>
                <div className={styles.metricValue}>
                  {analyticsData.thisWeek.count}
                </div>
                <div className={styles.metricLabel}>
                  Delivered this week
                </div>
              </div>
              <div className={styles.metric}>
                <div className={styles.metricChange} data-trend="up">
                  +{analyticsData.thisYear.change}%
                </div>
                <div className={styles.metricValue}>
                  {analyticsData.thisYear.count}
                </div>
                <div className={styles.metricLabel}>
                  Delivered this year
                </div>
              </div>
              <div className={styles.metric}>
                <div className={styles.metricChange} data-trend="up">
                  +{analyticsData.unread.change}%
                </div>
                <div className={styles.metricValue}>
                  {analyticsData.unread.count}
                </div>
                <div className={styles.metricLabel}>
                  Unread this year
                </div>
              </div>
            </div>

            <div className={styles.chartContainer}>
              <div className={styles.calendarHeading}>
                <div className={styles.calendarDays}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, i) => (
                    <span key={i} className={styles.dayName}>{day}</span>
                  ))}
                </div>
                <div className={styles.calendarDates}>
                  {['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'].map((day, i) => (
                    <span key={i} className={i === 4 ? styles.activeDay : styles.dayNumber}>{day}</span>
                  ))}
                </div>
              </div>
              <div className={styles.chartBox}>
                <div className={styles.chartWrapper}>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart
                      data={chartData}
                      margin={{
                        top: 20, right: 0, left: 0, bottom: 0,
                      }}
                    >
                      <defs>
                        <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6C5DD3" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#6C5DD3" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#8875FF" />
                          <stop offset="100%" stopColor="#5D45FF" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="#f0f0f0" strokeDasharray="3 3" opacity={0.5} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false}
                        hide
                      />
                      <YAxis hide />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className={styles.customTooltip}>
                                <p className={styles.tooltipValue}>{payload[0].value}</p>
                                <p className={styles.tooltipLabel}>messages sent</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                        cursor={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="none"
                        strokeWidth={0}
                        fillOpacity={1}
                        fill="url(#colorGradient)"
                      />
                      <Line 
                        type="natural" 
                        dataKey="value" 
                        stroke="url(#lineGradient)" 
                        strokeWidth={3} 
                        dot={false}
                        isAnimationActive={true}
                        animationDuration={1500}
                        animationEasing="ease-in-out"
                        activeDot={{ 
                          r: 6, 
                          fill: '#FFFFFF', 
                          stroke: '#6C5DD3', 
                          strokeWidth: 2,
                          strokeDasharray: "" 
                        }} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className={styles.xAxisLabels}>
                    {chartData.map((point, i) => (
                      <span key={i}>{point.name}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.dispatchHistory}>
            <h3>Dispatch History</h3>
            <div className={styles.historyContainer}>
              {dispatchHistory.map((item) => (
                <div key={item.id} className={styles.historyItem}>
                  {renderTypeIcon(item.type)}
                  <div className={styles.historyContent}>
                    <div className={styles.historyHeader}>
                      <h4>{item.title}</h4>
                    </div>
                    <span className={styles.historyTime}>{item.time}</span>
                  </div>
                  <div className={styles.historyStatus}>
                    {renderStatusBadge(item.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.actions}>
            <h3>Quick Actions</h3>
            <div className={styles.actionButtons}>
              {quickActions.map((action, index) => (
                <button key={index} className={styles.actionButton} onClick={action.action}>
                  <span className={styles.actionIcon}>{action.icon}</span>
                  <span className={styles.actionLabel}>{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.staffStatus}>
            <h3>Staff Status Notifications</h3>
            <div className={styles.statusContainer}>
              {staffStatus.map((staff) => (
                <div key={staff.id} className={styles.staffItem}>
                  <div className={styles.staffInfo}>
                    <div className={styles.staffHeader}>
                      <h4>{staff.name}</h4>
                      <div className={styles.staffStatusBadge}>
                        {staff.status === 'confirmed' ? (
                          <span className={styles.statusConfirmed}>Confirmed</span>
                        ) : staff.status === 'denied' ? (
                          <span className={styles.statusDenied}>Denied</span>
                        ) : (
                          <span className={styles.statusUnread}>Unread</span>
                        )}
                      </div>
                    </div>
                    <p className={styles.staffDetails}>{staff.details}</p>
                    <span className={styles.staffTime}>{staff.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Operations;
